import React, { useState, useRef, useEffect } from 'react';
import { Label } from './ui/label';
import { ChevronDown, X } from 'lucide-react';

export function ProductSearchSelect({ products, value, onChange, placeholder = 'Select a product', label = 'Product' }) {
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [filteredProducts, setFilteredProducts] = useState(products);
    const containerRef = useRef(null);
    const inputRef = useRef(null);

    const selectedProduct = products.find(p => p.id === value);

    // Smart search: matches against name, category, and SKU
    useEffect(() => {
        if (!searchTerm.trim()) {
            setFilteredProducts(products);
            return;
        }

        const term = searchTerm.toLowerCase();
        const filtered = products.filter(product => {
            const name = product.name?.toLowerCase() || '';
            const sku = product.sku?.toLowerCase() || '';
            const category = product.category?.name?.toLowerCase() || '';

            return (
                name.includes(term) ||
                sku.includes(term) ||
                category.includes(term)
            );
        });

        setFilteredProducts(filtered);
    }, [searchTerm, products]);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (productId) => {
        onChange(productId);
        setIsOpen(false);
        setSearchTerm('');
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChange('');
        setSearchTerm('');
    };

    return (
        <div ref={containerRef} className="space-y-2">
            {label && <Label>{label}</Label>}

            <div className="relative">
                {/* Trigger Button */}
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="w-full px-3 py-2 border border-input rounded-md bg-background text-left flex items-center justify-between hover:bg-accent/50 transition-colors"
                    type="button"
                >
                    <span className={selectedProduct ? 'text-foreground' : 'text-muted-foreground'}>
                        {selectedProduct ? selectedProduct.name : placeholder}
                    </span>
                    <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                </button>

                {/* Clear button overlay */}
                {selectedProduct && !isOpen && (
                    <button
                        onClick={handleClear}
                        className="absolute right-8 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        type="button"
                        tabIndex={-1}
                    >
                        <X className="h-4 w-4" />
                    </button>
                )}

                {/* Dropdown */}
                {isOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-background border border-input rounded-md shadow-lg z-50 max-h-64 overflow-hidden flex flex-col">
                        {/* Search Input */}
                        <input
                            ref={inputRef}
                            type="text"
                            placeholder="Search by name, SKU, or category..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            autoComplete="off"
                            className="px-3 py-2 border-b border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none text-sm"
                            autoFocus
                        />

                        {/* Results */}
                        <div className="overflow-y-auto flex-1">
                            {filteredProducts.length === 0 ? (
                                <div className="p-4 text-sm text-muted-foreground text-center">
                                    No products found
                                </div>
                            ) : (
                                filteredProducts.map(product => (
                                    <button
                                        key={product.id}
                                        onClick={() => handleSelect(product.id)}
                                        className="w-full text-left px-3 py-2.5 hover:bg-accent transition-colors border-b border-input/50 last:border-b-0"
                                        type="button"
                                        tabIndex={-1}
                                    >
                                        <div className="font-medium text-sm">{product.name}</div>
                                        <div className="text-xs text-muted-foreground mt-0.5">
                                            {product.sku}
                                            {product.category && ` • ${product.category.name}`}
                                        </div>
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
